import React from 'react';
import { Shield, Target, Bot, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * AnalysisLauncher Component
 * Provides buttons to launch different types of CTI analysis
 * 
 * @param {Object} props
 * @param {Function} props.onAnalyze - Callback when an analysis type is selected
 * @param {boolean} props.disabled - Whether the buttons should be disabled
 * @param {boolean} props.isLoading - Loading state
 * @param {string} props.analysisType - Currently running analysis type
 * @param {boolean} props.showAtlas - Whether to show the ATLAS button (based on AI components checkbox)
 */
const AnalysisLauncher = ({ onAnalyze, disabled, isLoading, analysisType, showAtlas = false, progress = null }) => {
    const { t } = useTranslation();

    const allButtons = [
        {
            type: 'nist',
            label: t('workshop4.analysisTypes.nist'),
            logo: (
                <svg className="h-8 w-auto" viewBox="0 0 192.756 192.756" xmlns="http://www.w3.org/2000/svg">
                    <g fill="#1e40af" fillRule="evenodd" clipRule="evenodd">
                        <path d="M59.26 120.537c5.71 0 9.883-5.051 9.883-10.762V72.218H59.26v35.801c0 .877-.879 1.756-1.757 1.756 0 0-.659 0-.659-.879L26.315 76.392c-2.636-2.416-5.93-4.173-9.225-4.173h-.659c-5.93 0-10.762 5.052-10.762 10.762v37.557h10.762V83.859s0-.878.659-.878h1.757l30.529 33.385c1.538 2.416 4.832 3.295 8.126 4.172h1.758v-.001zM104.725 101.43h31.188c1.758 0 3.295 1.537 3.295 4.174 0 2.416-1.537 4.172-3.295 4.172h-47.88c-1.537 0-2.416-.879-2.416-2.635V72.218h-9.883v34.043c0 7.688 5.71 14.275 13.178 14.275h47.88c7.467 0 13.178-6.588 13.178-14.275v-1.537c0-7.469-6.59-13.398-13.178-13.398h-30.529c-3.295-.22-3.953-2.196-4.174-4.173v-.879c0-1.537 1.758-3.294 4.174-3.294h50.295v37.557h9.885V82.98h20.645V72.218h-82.361c-7.469 0-13.179 6.589-13.179 13.398v2.416c-.002 7.468 5.708 13.398 13.177 13.398z"
                            stroke="#1e40af" strokeWidth="0.5" strokeMiterlimit="2.613" />
                    </g>
                </svg>
            ),
            fallbackIcon: <Shield className="h-8 w-8 text-blue-800" />,
            color: 'bg-white hover:bg-gray-50 text-blue-700 border-2 border-gray-300 shadow-md',
            description: t('workshop4.analysisLauncher.nistDescription')
        },
        {
            type: 'euvd',
            label: t('workshop4.analysisTypes.euvd'),
            logo: (
                <img
                    src="/cti_logos/ENISALogoLARGE.png"
                    alt="ENISA/EUVD Logo"
                    className="h-8 w-auto object-contain"
                    onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                    }}
                />
            ),
            fallbackIcon: <Shield className="h-8 w-8 text-amber-900" />,
            color: 'bg-amber-50 hover:bg-amber-100 text-amber-950 border-2 border-amber-300 shadow-md',
            description: t('workshop4.analysisLauncher.euvdDescription')
        },
        {
            type: 'mitre',
            label: t('workshop4.analysisTypes.mitre'),
            logo: (
                <img
                    src="/cti_logos/MITRE_ATTACK_logo_Lockup-black.svg"
                    alt="MITRE ATT&CK Logo"
                    className="h-7 w-auto object-contain brightness-0 invert"
                    onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                    }}
                />
            ),
            fallbackIcon: <Target className="h-8 w-8" />,
            color: 'bg-blue-600 hover:bg-blue-700 text-white',
            description: t('workshop4.analysisLauncher.mitreDescription')
        },
        {
            type: 'atlas',
            label: t('workshop4.analysisTypes.atlas'),
            logo: (
                <img
                    src="/cti_logos/mitre_atlas_logo.svg"
                    alt="MITRE ATLAS Logo"
                    className="h-8 w-auto object-contain brightness-0 invert"
                    onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                    }}
                />
            ),
            fallbackIcon: <Bot className="h-8 w-8" />,
            color: 'bg-purple-600 hover:bg-purple-700',
            description: t('workshop4.analysisLauncher.atlasDescription')
        },
    ];

    // Filter buttons based on AI components checkbox
    const buttons = showAtlas ? allButtons : allButtons.filter(btn => btn.type !== 'atlas');

    // Dynamic grid layout based on number of buttons
    const getGridLayout = () => {
        const buttonCount = buttons.length;
        if (buttonCount === 4) {
            // 4 buttons (with ATLAS): 2x2 on mobile, 4 columns on desktop
            return "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4";
        } else if (buttonCount === 3) {
            // 3 buttons (without ATLAS): 1 column on mobile, 3 columns on desktop
            return "grid grid-cols-1 sm:grid-cols-1 md:grid-cols-3 gap-4";
        }
        // Fallback for other counts
        return "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4";
    };

    return (
        <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="mb-6 text-center">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">{t('workshop4.analysisLauncher.title')}</h3>
                <p className="text-sm text-gray-600">
                    {t('workshop4.analysisLauncher.description')}
                    {showAtlas && (
                        <span className="block mt-1 text-xs text-purple-600 font-medium">
                            🤖 {t('workshop4.analysisLauncher.aiModeActive')}
                        </span>
                    )}
                </p>
            </div>

            <div className={getGridLayout()}>
                {buttons.map(btn => (
                    <div key={btn.type} className="group">
                        <button
                            onClick={() => onAnalyze(btn.type)}
                            disabled={disabled || isLoading}
                            className={`w-full flex flex-col items-center justify-center p-4 rounded-lg text-sm font-semibold transition-all duration-200 disabled:bg-gray-300 disabled:cursor-not-allowed ${btn.color} shadow-md hover:shadow-lg transform hover:-translate-y-0.5 hover:scale-102 min-h-[110px] max-w-xs mx-auto`}
                        >
                            <div className="flex items-center justify-center mb-2">
                                {isLoading && analysisType === btn.type ?
                                    <Loader2 className="h-6 w-6 animate-spin" /> :
                                    <div className="flex items-center justify-center h-8">
                                        {btn.logo}
                                        <div style={{ display: 'none' }} className="text-2xl opacity-90">{btn.fallbackIcon}</div>
                                    </div>
                                }
                            </div>

                            <div className="text-center">
                                <div className="font-bold mb-1 text-sm">
                                    {isLoading && analysisType === btn.type ? t('workshop4.analysisLauncher.analyzing') : btn.label}
                                </div>
                                <div className={`text-xs leading-snug px-1 ${(btn.type === 'mitre' || btn.type === 'atlas') ? 'text-white opacity-75' : 'opacity-75'}`}>
                                    {btn.description}
                                </div>
                            </div>
                        </button>

                        {/* Per-asset progress */}
                        {isLoading && analysisType === btn.type && progress && progress.total > 0 && (
                            <div className="mt-2">
                                <div className="flex justify-between text-xs text-gray-600 mb-1">
                                    <span className="truncate">{progress.assetName}</span>
                                    <span>{progress.current}/{progress.total}{progress.cacheHit ? ' (cache)' : ''}</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                                    <div
                                        className="bg-indigo-600 h-1.5 rounded-full transition-all duration-300"
                                        style={{ width: `${Math.round((progress.current / progress.total) * 100)}%` }}
                                    />
                                </div>
                                <div className="text-xs text-gray-500 mt-1 text-center truncate">
                                    {progress.status === 'cache-hit' ? '⚡ Cache' : progress.status === 'fetching' ? t('workshop4.analysisLauncher.processing') : progress.status}
                                    {progress.source ? ` · ${progress.source.toUpperCase()}` : ''}
                                </div>
                            </div>
                        )}
                        {isLoading && analysisType === btn.type && (!progress || !progress.total) && (
                            <div className="mt-2 text-center">
                                <div className="inline-flex items-center text-xs text-gray-600">
                                    <div className="animate-pulse w-2 h-2 bg-indigo-500 rounded-full mr-1"></div>
                                    {t('workshop4.analysisLauncher.processing')}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {disabled && (
                <div className="mt-4 text-center">
                    <p className="text-sm text-orange-600 bg-orange-50 border border-orange-200 rounded-lg p-3">
                        ⚠️ {t('workshop4.analysisLauncher.selectAssetWarning')}
                    </p>
                </div>
            )}
        </div>
    );
};

export default AnalysisLauncher;